"use client";
import { useEffect, useRef, useState } from "react";

const waiting = new Map<Element, () => void>();
let observer: IntersectionObserver | undefined;
function observe(element: Element, reveal: () => void) {
  observer ??= new IntersectionObserver((entries) => {
    for (const entry of entries) if (entry.isIntersecting) {
      const show = waiting.get(entry.target);
      waiting.delete(entry.target);
      observer?.unobserve(entry.target);
      show?.();
    }
  }, { rootMargin: "100px" });
  waiting.set(element, reveal);
  observer.observe(element);
  return () => {
    waiting.delete(element);
    observer?.unobserve(element);
    if (!waiting.size) { observer?.disconnect(); observer = undefined; }
  };
}

export function AttachPreviewImage({ src, fallback, alt = "" }: { src: string; fallback?: string; alt?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);
  const [failedSrc, setFailedSrc] = useState<string>();
  useEffect(() => {
    if (!ref.current) return;
    if (typeof IntersectionObserver === "undefined") {
      const timer = window.setTimeout(() => setVisible(true), 0);
      return () => window.clearTimeout(timer);
    }
    return observe(ref.current, () => setVisible(true));
  }, []);
  return <span ref={ref} className="absolute inset-0 block size-full">
    {visible ? (
      // eslint-disable-next-line @next/next/no-img-element
      <img src={failedSrc === src && fallback ? fallback : src} alt={alt}
        loading="lazy" decoding="async" className="size-full object-contain"
        onError={() => setFailedSrc(src)} />
    ) : null}
  </span>;
}
